import { useState } from "react";
import { IconBuildingBank, IconPencil, IconPlus, IconWorld } from "@tabler/icons-react";
import { Badge, Button, Card, Divider, Group, Heading, Input, NumberInput, Stack, Text, ThemeIcon } from "@/components/ui";
import { toastError } from "@/lib/toast";
import { useCrearProveedor } from "../hooks/use-proveedores-mutations";
import type { TipoProveedor } from "./SeleccionarTipoProveedorModal";

export function ProveedorCrearForm({
  numeroSugerido,
  tipo,
  onSalir,
  onCambiarTipo,
  rfcInicial = "",
  razonSocialInicial = "",
}: {
  numeroSugerido: number;
  /** Prellenado (p. ej. desde una solicitud de alta de catálogo). */
  rfcInicial?: string;
  razonSocialInicial?: string;
  tipo: TipoProveedor;
  onSalir: () => void;
  /** Reabre el modal para cambiar entre nacional/extranjero. */
  onCambiarTipo: () => void;
}) {
  const [numeroProveedor, setNumeroProveedor] = useState<number | "">(numeroSugerido);
  const [rfc, setRfc] = useState(rfcInicial.toUpperCase());
  const [razonSocial, setRazonSocial] = useState(razonSocialInicial);
  const [banco, setBanco] = useState("");
  const [cuenta, setCuenta] = useState("");
  const [clabe, setClabe] = useState("");

  const esExtranjero = tipo === "extranjero";
  const crear = useCrearProveedor({ onCreado: onSalir });

  const submit = () => {
    const numero = Number(numeroProveedor);
    if (!numero || numero <= 0) {
      toastError({ title: "Datos incompletos", message: "Número de proveedor debe ser positivo." });
      return;
    }
    if (!rfc.trim() || !razonSocial.trim()) {
      const idLabel = esExtranjero ? "identificador fiscal" : "RFC";
      toastError({ title: "Datos incompletos", message: `${idLabel} y razón social son obligatorios.` });
      return;
    }
    // La validación de formato mexicano solo aplica al nacional · el extranjero
    // usa un tax ID libre. El backend revalida en ambos casos.
    if (!esExtranjero && (rfc.trim().length < 12 || rfc.trim().length > 13)) {
      toastError({ title: "RFC inválido", message: "Un RFC mexicano debe tener 12 o 13 caracteres. Si es un proveedor extranjero, cambia el tipo a Extranjero." });
      return;
    }
    crear.mutate({
      numeroProveedor: numero,
      esExtranjero,
      rfc: rfc.trim().toUpperCase(),
      razonSocial: razonSocial.trim(),
      banco: banco.trim() || null,
      cuenta: cuenta.trim() || null,
      // La CLABE no aplica a proveedores extranjeros.
      clabe: esExtranjero ? null : clabe.trim() || null,
    });
  };

  return (
    <Card padding="lg">
      <Stack gap="md">
        <Group justify="space-between" align="flex-start" wrap="nowrap">
          <Group gap="sm" align="flex-start" wrap="nowrap">
            <ThemeIcon size="md" radius="md" variant="filled" color="multiva">
              {esExtranjero ? <IconWorld size={20} stroke={1.6} /> : <IconBuildingBank size={20} stroke={1.6} />}
            </ThemeIcon>
            <div>
              <Group gap="xs" align="center">
                <Heading order={5}>Nuevo proveedor</Heading>
                <Badge variant="light" color={esExtranjero ? "slate" : "multiva"}>
                  {esExtranjero ? "Extranjero" : "Nacional"}
                </Badge>
              </Group>
              <Text size="sm" c="dimmed" className="mt-0.5">
                Número de proveedor único ·{" "}
                {esExtranjero ? "identificador fiscal extranjero (tax ID)" : "RFC mexicano"} · datos bancarios (opcionales).
              </Text>
            </div>
          </Group>
          <Button variant="subtle" size="sm" leftSection={<IconPencil size={14} />} onClick={onCambiarTipo}>
            Cambiar tipo
          </Button>
        </Group>
        <Divider />
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          <NumberInput label="Número de proveedor" min={1} value={numeroProveedor} onChange={(v) => setNumeroProveedor(typeof v === "number" ? v : "")} />
          <Input
            label={esExtranjero ? "Identificador de proveedor extranjero" : "RFC"}
            placeholder={esExtranjero ? "109012 / VAT / EIN" : "ACM010101AAA"}
            value={rfc}
            onChange={(e) => setRfc(e.currentTarget.value.toUpperCase())}
            maxLength={esExtranjero ? 40 : 13}
            className="font-mono"
          />
          <Input label="Razón social" placeholder={esExtranjero ? "SWIFT SC" : "ACME SA de CV"} value={razonSocial} onChange={(e) => setRazonSocial(e.currentTarget.value)} />
          <Input label="Banco (opcional)" placeholder={esExtranjero ? "JPMorgan Chase" : "BBVA"} value={banco} onChange={(e) => setBanco(e.currentTarget.value)} />
          <Input label="Cuenta (opcional)" value={cuenta} onChange={(e) => setCuenta(e.currentTarget.value)} className="font-mono" />
          {/* La CLABE es un dato bancario mexicano · no aplica a proveedores extranjeros. */}
          {!esExtranjero && (
            <Input label="CLABE (opcional)" value={clabe} onChange={(e) => setClabe(e.currentTarget.value)} maxLength={18} className="font-mono" />
          )}
        </div>
        <Group justify="flex-end">
          <Button variant="default" onClick={onSalir}>
            Cancelar
          </Button>
          <Button leftSection={<IconPlus size={14} />} loading={crear.isPending} onClick={submit}>
            Crear
          </Button>
        </Group>
      </Stack>
    </Card>
  );
}
